import dayjs, { Dayjs } from 'dayjs';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { OpenAccessProtocol, OpenAccessProtocolType } from './open-access-protocol.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { Duration } from 'dayjs/plugin/duration.js';
import { VersionRange } from '../../core/version-range.js';

function calculateExpirationDate(renewalDate: Dayjs, renewalPeriod: GymPassRenewalPeriod, timeZoneRegion: TimeZoneRegion): Dayjs {
  switch(renewalPeriod) {
    case GymPassRenewalPeriod.OneDay: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureDayAtFirstHour(1, renewalDate).subtract(1, 'second');
    }
    case GymPassRenewalPeriod.OneWeek: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureDayAtFirstHour(7, renewalDate).subtract(1, 'second');
    }
    case GymPassRenewalPeriod.OneMonth: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureMonthOnSameDayAtFirstHour(1, { today: renewalDate }).subtract(1, 'second');
    }
    case GymPassRenewalPeriod.OneYear: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureYearOnSameDayAtFirstHour(1, { today: renewalDate }).subtract(1, 'second');
    }
    case GymPassRenewalPeriod.ThreeMonths: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureMonthOnSameDayAtFirstHour(3, { today: renewalDate }).subtract(1, 'second');
    }
    case GymPassRenewalPeriod.TwoMonths: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureMonthOnSameDayAtFirstHour(2, { today: renewalDate }).subtract(1, 'second');
    }
    case GymPassRenewalPeriod.SixMonths: {
      const timezone = TimeZone.ofRegion(timeZoneRegion);
      return timezone.getFutureMonthOnSameDayAtFirstHour(6, { today: renewalDate }).subtract(1, 'second');
    }
  }
}

export enum GymPassRenewalPeriod {
  OneDay = 'OneDay',
  OneWeek = 'OneWeek',
  OneMonth = 'OneMonth',
  OneYear = 'OneYear',
  ThreeMonths = 'ThreeMonths',
  TwoMonths = 'TwoMonths',
  SixMonths = 'SixMonths',
}

export class GymAccessProtocol extends OpenAccessProtocol {
  static readonly TYPE_ID: string = 'c243b9926ec447b2855165e7d462f3fc';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly type: OpenAccessProtocolType = OpenAccessProtocolType.GymAccess;
  public readonly expirationDate: Dayjs;
  private _uses: number | null;

  private constructor(
    public readonly startDate: Dayjs,
    public readonly renewalDate: Dayjs,
    public readonly scanValidity: Duration,
    public readonly maxUses: number | null,
    public readonly timezone: TimeZoneRegion,
    public readonly renewalPeriod: GymPassRenewalPeriod,
    uses: number | null,
  ) {
    super();
    this._uses = uses;
    this.expirationDate = calculateExpirationDate(renewalDate, renewalPeriod, timezone);
  }

  get $typeId(): string {
    return GymAccessProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, GymAccessProtocol.SCHEMA_VERSION);
  }

  get uses(): number | null {
    return this._uses;
  }

  get isUsed(): boolean {
    return this._uses != null && this.maxUses != null && this._uses >= this.maxUses;
  }

  static fromMap(context: SerializationContext, map: SerializedData): GymAccessProtocol {
    return new GymAccessProtocol(
        context.decodeDate(MapUtils.get<number>(map, 'startDate')),
        context.decodeDate(MapUtils.get<number>(map, 'renewalDate')),
        context.decodeDuration(MapUtils.get<number>(map, 'scanValidity')),
        MapUtils.getOrNull<number>(map, 'maxUses'),
        toEnumFromString(MapUtils.get<string>(map, 'timezone'), TimeZoneRegion),
        toEnumFromString(MapUtils.get<string>(map, 'renewalPeriod'), GymPassRenewalPeriod),
        MapUtils.getOrNull<number>(map, 'uses'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'startDate': context.encodeDate(this.startDate),
      'renewalDate': context.encodeDate(this.renewalDate),
      'scanValidity': context.encodeDuration(this.scanValidity),
      'expirationDate': context.encodeDate(this.expirationDate),
      'maxUses': this.maxUses,
      'uses': this._uses,
      'renewalPeriod': fromEnumAsString(this.renewalPeriod),
      'timezone': fromEnumAsString(this.timezone),
    };
  }

  clone(): this {
    return new GymAccessProtocol(
        TimeZone.cloneDate(this.startDate),
        TimeZone.cloneDate(this.renewalDate),
        TimeZone.cloneDuration(this.scanValidity),
        this.maxUses,
        this.timezone,
        this.renewalPeriod,
        this._uses,
    ) as this;
  }

  isSame(other: GymAccessProtocol): boolean {
    if(other === this) {
      return true;
    }

    return other.renewalPeriod == this.renewalPeriod &&
        other.timezone == this.timezone &&
        other.startDate == this.startDate &&
        other.renewalDate == this.renewalDate &&
        other.scanValidity == this.scanValidity &&
        other.maxUses == this.maxUses &&
        other._uses == this._uses;
  }

  copyWith(data: Partial<GymAccessProtocol>): this {
    return new GymAccessProtocol(
        data.startDate ?? TimeZone.cloneDate(this.startDate),
        data.renewalDate ?? TimeZone.cloneDate(this.renewalDate),
        data.scanValidity ?? TimeZone.cloneDuration(this.scanValidity),
        data.maxUses ?? this.maxUses,
        data.timezone ?? this.timezone,
        data.renewalPeriod ?? this.renewalPeriod,
        data.uses ?? this._uses,
    ) as this;
  }

  applyTemplateChanges(other: this): this {
    return this.clone();
  }

  updateUsage(): void {
    if(this.maxUses != null) {
      this._uses = Math.min(this.maxUses, (this._uses ?? 0) + 1);
    }
  }

  isEligibleForRenewal(currentDate: Dayjs): boolean {
    const minRenewalDate = TimeZone.ofRegion(this.timezone).getPastDayAtFirstHour(2, this.expirationDate);
    return currentDate.isAfter(minRenewalDate);
  }

  renew(renewalDate: Dayjs): this {
    return new GymAccessProtocol(
        TimeZone.cloneDate(this.startDate),
        TimeZone.cloneDate(renewalDate),
        TimeZone.cloneDuration(this.scanValidity),
        this.maxUses,
        this.timezone,
        this.renewalPeriod,
        null,
    ) as this;
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof GymAccessProtocol;
  }

  static assertType(dm: DataModel): dm is GymAccessProtocol {
    return dm instanceof GymAccessProtocol;
  }
}

export class GymAccessProtocolBuilder extends DataModelBuilder<GymAccessProtocol> {
  build(context: SerializationContext, map: SerializedData): GymAccessProtocol {
    return GymAccessProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(GymAccessProtocol.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GymAccessProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      map['scanValidity'] = TimeZone.encodeDuration(dayjs.duration(5, 'hours'));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
